import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import managementStyles from '../ManagementPages.module.css';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { Nav } from 'react-bootstrap';
import { setDeviceRegistryData } from './DeviceRegistrySlice';
import FilterManagementView from '../../components/FilterManagementView';
import { OnlineIcon, OfflineIcon } from '../../components/icons/Icons';
import { configureBatteryIcon } from '../../utils/batteryUtils';

const DeviceRegistry = () => {
    const [deviceData, setDeviceData] = useState([]);
    const [activeTab, setActiveTab] = useState('catalog');
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userId = useSelector((state) => state.userInfo.id);
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const onRowClickHandler = (device) => {
        // Create a clean version of the device data without React elements
        const cleanDevice = {
            ...device,
            status: device.statusText, // Use the text version instead of the JSX
        };
        
        navigate('/device-information', { 
            state: { deviceData: cleanDevice }
        });
    };

    const headers = [
        { key: 'id', label: "ID" },
        { key: 'serialNumber', label: "Serial Number" },
        { key: 'organizationName', label: "Organization Assigned" },
        { key: 'macAddress', label: "Mac Address" },
        { key: 'status', label: "Status" },
        { key: 'dateAssigned', label: "Date Assigned" },
    ];

    useEffect(() => {
        getDevices();
    }, [httpService, userId]);

    const getDevices = async () => {
        try {
            const responseData = await httpService.get(`device/all/${userId}`);
            if (Array.isArray(responseData.data.data)) {
                let response = [...responseData.data.data];
                for (const item of response) {
                    // Handle null values
                    item.patientId = item.patientId || "-";
                    
                    // Always display organization name
                    item.organizationName = item.Organization_Name || "-";
                    
                    item.serialNumber = item.serialNumber || "-";
                    item.macAddress = item.macAddress || "-";
                    
                    // Format date with time or set to N/A if null
                    if (item.dateAssigned) {
                        const dateUTC = new Date(item.dateAssigned);
                        const dateLocal = new Date(dateUTC.getTime() - (dateUTC.getTimezoneOffset() * 60000));
                        item.dateAssigned = dateLocal.toLocaleString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                        });
                    } else {
                        item.dateAssigned = "-";
                    }

                    // Add default status handling for non-Perin Patch devices
                    if (item.deviceName !== "Perin Patch") {
                        const isOnline = item.isActive;
                        item.statusText = isOnline ? "Online" : "Offline"; // Store plain text version
                        item.status = (
                            <span style={{ display: "flex", alignItems: "center", columnGap: "5px" }}>
                                {isOnline ? <OnlineIcon /> : <OfflineIcon />}
                                {item.statusText}
                            </span>
                        );
                    }

                    // Check if the device is a Perin Patch to add battery level information
                    if (item.deviceName === "Perin Patch") {
                        const batteryLevel = item.batteryLevel;
                        const isDecimal = batteryLevel !== -1 && batteryLevel && batteryLevel <= 1;
                        const normalizedLevel = isDecimal ? Math.floor(batteryLevel * 100) : Math.floor(batteryLevel);
                        item.statusText = batteryLevel !== -1 && batteryLevel ? 
                            `${normalizedLevel}%` 
                            : "-";
                        const batteryIcon = configureBatteryIcon(batteryLevel);
                        item.status = (
                            <span style={{ display: "flex", alignItems: "center", columnGap: "5px" }}>
                                {(batteryLevel === -1 || !batteryLevel) ?
                                    "-" : batteryIcon
                                }
                                {item.statusText}
                            </span>
                        );
                    }
                }
                setDeviceData(response);
            } else {
                console.error('Invalid data format:');
            }
        } catch (err) {
            console.log(err);
        }
    };
    
    const tabStyle = (isActive) => ({
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 4,
        paddingBottom: 4,
        background: isActive ? 'rgba(203, 243, 210, 0.30)' : '#F3F3F3',
        borderBottom: isActive ? '3px #006969 solid' : '0.50px #B9B9B9 solid',
        cursor: 'pointer'
    });

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={false}
                isGroup={false}
                isSubGroup={false}
                isTeam={false}
                showDeleteButton={false}
                showAddButton={false}
                modelHeader='Delete Device?' 
            />
            <div className={managementStyles.GridContainer}>
                {error && <div style={{ color: 'red' }}>{error}</div>}
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <PerinHealthGrid
                        dataList={deviceData}
                        headers={headers}
                        showCheckbox={true}
                        showEditIcon={false}
                        onRowValueClick={onRowClickHandler}
                        rowValueAction="serialNumber"
                        showHeaderFilter={true}
                        gridHeight="100%"
                    />
                )}
            </div>
        </div>
    );
};

export default DeviceRegistry;